import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AdminOrderActionRequest,
  AdminOrderActionResponse,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { orderQueryKey } from './useOrderQuery';

export type OrderActionType =
  | 'cancel-approve'
  | 'cancel-reject'
  | 'fulfillment-ready'
  | 'fulfillment-fail'
  | 'fulfillment-complete'
  | 'pickup-confirm';

const ACTION_TO_PATH: Record<
  OrderActionType,
  | '/admin/v1/orders/{orderId}/cancel-approve'
  | '/admin/v1/orders/{orderId}/cancel-reject'
  | '/admin/v1/orders/{orderId}/fulfillment-ready'
  | '/admin/v1/orders/{orderId}/fulfillment-fail'
  | '/admin/v1/orders/{orderId}/fulfillment-complete'
  | '/admin/v1/orders/{orderId}/pickup-confirm'
> = {
  'cancel-approve': '/admin/v1/orders/{orderId}/cancel-approve',
  'cancel-reject': '/admin/v1/orders/{orderId}/cancel-reject',
  'fulfillment-ready': '/admin/v1/orders/{orderId}/fulfillment-ready',
  'fulfillment-fail': '/admin/v1/orders/{orderId}/fulfillment-fail',
  // QA-371 — 배송완료(FULFILLING → FULFILLED). BE 에 있으나 generated 스펙에 아직 없다(요청·응답은 다른 액션과 같음).
  'fulfillment-complete': '/admin/v1/orders/{orderId}/fulfillment-complete',
  'pickup-confirm': '/admin/v1/orders/{orderId}/pickup-confirm',
};

export interface OrderActionInput {
  orderId: string;
  action: OrderActionType;
  body: AdminOrderActionRequest;
}

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Order action failed with status ${status}`, raw);
};

export const useOrderActionMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminOrderActionResponse, Error, OrderActionInput>({
    mutationFn: async ({ orderId, action, body }) => {
      const path = ACTION_TO_PATH[action];
      const concretePath = path.replace('{orderId}', orderId);
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body);
      // fulfillment-complete 는 generated 경로 타입에 없어 같은 모양의 타입드 경로로 맞춘다(요청·응답 형태 동일).
      const typedPath = path as Exclude<typeof path, '/admin/v1/orders/{orderId}/fulfillment-complete'>;
      const { data, error, response } = await apiClient.raw.POST(typedPath, {
        body,
        params: {
          path: { orderId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: AdminOrderActionResponse }).data;
    },
    onSuccess: async (_data, { orderId }) => {
      await qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
